import { FC, useCallback, useEffect, useState } from 'react'
import { 
  Alert, Box, Typography, Grid2, Paper, alpha, Avatar, Button, 
  Card, CardActionArea, CardContent, CircularProgress, Divider, IconButton, Tooltip 
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import { 
  MdPeople, MdLocalShipping, MdSettings,
  MdBrush, MdNavigateNext, MdOutlineDashboard, MdLabel,
  MdOutlinePrecisionManufacturing, MdVerifiedUser, MdOutlineAccountTree
} from 'react-icons/md'
import { IoRefreshOutline } from 'react-icons/io5'
import { useAuth } from '../../../Hooks/Auth'
import { useToast } from '../../../Hooks/ToasContext'
import { Environment } from '../../../Config/constants'
import api from '../../../Services/api'
import { Can } from '../../../Components/Can'

// ─── Componentes Internos ───────────────────────────────────────────────────

const IndicatorCard: FC<{
  title: string
  value: string | number
  icon: React.ReactNode
  color: string
  loading?: boolean
  helperText?: string
}> = ({
  title, value, icon, color, loading, helperText,
}) => {
  const theme = useTheme()
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        height: '100%',
        borderRadius: 3,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 8px 24px ${alpha(color, 0.12)}`,
          borderColor: color
        }
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Box sx={{ 
          p: 1.5, 
          borderRadius: 2, 
          bgcolor: alpha(color, 0.1), 
          color: color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {icon}
        </Box>
      </Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {title}
      </Typography>
      <Box sx={{ mt: 0.5 }}>
        {loading ? (
          <CircularProgress size={20} sx={{ color }} />
        ) : (
          <>
            <Typography variant="h4" fontWeight={900} color="text.primary">
              {value}
            </Typography>
            {helperText && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {helperText}
              </Typography>
            )}
          </>
        )}
      </Box>
      <Box sx={{ 
        position: 'absolute', 
        right: -10, 
        bottom: -10, 
        opacity: 0.05, 
        transform: 'rotate(-15deg)',
        fontSize: '4rem',
        color: color
      }}>
        {icon}
      </Box>
    </Paper>
  )
}

const ShortcutCard: FC<{ 
  title: string; 
  subtitle: string; 
  icon: React.ReactNode; 
  path: string; 
  color: string;
  badge?: string | number;
  badgeColor?: string;
  disabled?: boolean;
}> = ({ 
  title, subtitle, icon, path, color, badge, badgeColor, disabled 
}) => {
  const navigate = useNavigate()
  return (
    <Card 
      variant="outlined" 
      sx={{ 
        height: '100%', 
        borderRadius: 3,
        opacity: disabled ? 0.7 : 1,
        transition: 'all 0.2s ease',
        '&:hover': { 
          borderColor: disabled ? 'divider' : color, 
          bgcolor: disabled ? 'transparent' : alpha(color, 0.02),
          transform: disabled ? 'none' : 'translateY(-2px)'
        }
      }}
    >
      <CardActionArea 
        onClick={() => !disabled && navigate(path)} 
        disabled={disabled}
        sx={{ height: '100%', p: 1 }}
      >
        <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'center', position: 'relative' }}>
          <Box sx={{ 
            p: 1.5, 
            borderRadius: '12px', 
            bgcolor: alpha(color, 0.1), 
            color: color,
            display: 'flex'
          }}>
            {icon}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" fontWeight={800} sx={{ lineHeight: 1.2 }}>{title}</Typography>
            <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
          </Box>
          
          {badge !== undefined && (
            <Box sx={{ 
              px: 1.2, 
              py: 0.4, 
              borderRadius: 1.5, 
              bgcolor: badgeColor || color, 
              color: 'white',
              fontSize: '0.65rem',
              fontWeight: 900,
              boxShadow: `0 4px 8px ${alpha(badgeColor || color, 0.2)}`
            }}>
              {badge}
            </Box>
          )}

          {!disabled && <MdNavigateNext size={20} color={alpha('#000', 0.2)} />}
        </CardContent>
      </CardActionArea>
    </Card>
  )
}

// ─── Componente Principal ────────────────────────────────────────────────────

const HCPDashboard: FC = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { showToast } = useToast()

  const [stats, setStats] = useState({
    collaborators: 0,
    arteFinalCount: 0,
    montagemCount: 0,
    loading: true
  })
  const [loadError, setLoadError] = useState(false)
  const [failedModules, setFailedModules] = useState<string[]>([])

  const fetchDashboardData = useCallback(async () => {
    setStats((prev) => ({ ...prev, loading: true }))
    setLoadError(false)
    setFailedModules([])

    const failures: string[] = []
    const listPageSize = Number(Environment.OFFSET_REGISTERS) || 50

    /** Indicadores opcionais: manter o dashboard disponível quando a consulta falhar. */
    const fetchOptional = async <T,>(request: () => Promise<T>, fallback: T): Promise<T> => {
      try {
        return await request()
      } catch (error) {
        console.error('error fetchHcpDashboard (optional indicator):', error)
        return fallback
      }
    }

    const fetchRequired = async <T,>(
      label: string,
      request: () => Promise<T>,
      fallback: T
    ): Promise<T> => {
      try {
        return await request()
      } catch (error) {
        console.error(`error fetchHcpDashboard (${label}):`, error)
        failures.push(label)
        return fallback
      }
    }

    const fetchArteFinalCount = async (): Promise<number> => {
      const fromHcpFlow = await fetchOptional(async () => {
        const response = await api.get('/order-products/hcp-flow/AWAITING_ART/kanban', {
          params: { page: 1, offset: 1 },
        })
        const payload = response.data
        const columns = payload?.columns ?? []

        if (columns.length > 0) {
          return columns
            .filter((column: { sequence?: number; name?: string }) => {
              const name = String(column.name ?? '').toLowerCase()
              return column.sequence !== 8 && !name.includes('aprovado (final)') && !name.includes('arquivado')
            })
            .reduce(
              (acc: number, column: { total?: number; count?: number; cards?: unknown[] }) =>
                acc + (column.total ?? column.count ?? column.cards?.length ?? 0),
              0
            )
        }

        return Number(payload?.total ?? 0)
      }, null as number | null)

      if (fromHcpFlow !== null) {
        return fromHcpFlow
      }

      return fetchOptional(async () => {
        const response = await api.get('/customization-flow/kanban')
        const columns = response.data?.columns ?? []
        return columns
          .filter((column: { key: string }) => column.key !== 'aprovado_final')
          .reduce((acc: number, column: { count?: number }) => acc + (column.count || 0), 0)
      }, 0)
    }

    const fetchMontagemCount = async (): Promise<number> => {
      const fromSheetFlow = await fetchOptional(async () => {
        const response = await api.get('/order-products/hcp-flow/SHEET/kanban', {
          params: { page: 1, offset: 1 },
        })
        return Number(response.data?.total ?? 0)
      }, null as number | null)

      if (fromSheetFlow !== null) {
        return fromSheetFlow
      }

      return fetchOptional(async () => {
        const response = await api.get('/customization-flow/kanban/column', {
          params: { status: 'aprovado_final', tem_os: false, page: 1, page_size: 1 },
        })
        return Number(response.data?.count ?? 0)
      }, 0)
    }

    try {
      const [collaboratorsTotal, arteCount, montagemCount] = await Promise.all([
        fetchRequired('Equipe HCP', async () => {
          const response = await api.get('/collaborators', {
            params: { page: 1, offset: listPageSize },
          })
          return Number(response.data?.total ?? response.data?.data?.length ?? 0)
        }, 0),
        fetchArteFinalCount(),
        fetchMontagemCount(),
      ])

      setStats({
        collaborators: collaboratorsTotal,
        arteFinalCount: arteCount,
        montagemCount,
        loading: false,
      })

      if (failures.length > 0) {
        setLoadError(true)
        setFailedModules(failures)
      }
    } catch (error) {
      console.error('error fetchHcpDashboard:', error)
      setStats((prev) => ({ ...prev, loading: false }))
      setLoadError(true)
      setFailedModules(['Dashboard HCP'])
      showToast('Não foi possível carregar o dashboard. Tente novamente.', 'error')
    }
  }, [showToast])

  useEffect(() => {
    fetchDashboardData()
  }, [fetchDashboardData])

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1400, margin: '0 auto' }}>
      
      {/* Banner de Boas-vindas */}
      <Paper 
        elevation={0}
        sx={{ 
          p: { xs: 3, md: 5 }, 
          borderRadius: 4, 
          bgcolor: 'primary.main',
          backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          mb: 5,
          display: 'flex',
          flexDirection: 'column',
          gap: 1
        }}
      >
        <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 3 }}>
          <Tooltip title="Atualizar indicadores">
            <span>
              <IconButton
                onClick={fetchDashboardData}
                disabled={stats.loading}
                size="small"
                sx={{
                  color: 'white',
                  bgcolor: 'rgba(255,255,255,0.12)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                }}
                aria-label="Atualizar dashboard HCP"
              >
                <IoRefreshOutline size={22} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        <Box sx={{ position: 'relative', zIndex: 2, pr: { xs: 0, sm: 8 } }}>
          <Typography variant="overline" sx={{ opacity: 0.85, fontWeight: 700, letterSpacing: 1.5, lineHeight: 1.2 }}>
            Portal do Administrador
          </Typography>
          <Typography variant="h5" fontWeight={800} sx={{ mt: 0.25, letterSpacing: -0.3 }}>
            {/* Formatação do nome: remove @..., remove pontos, e capitaliza */}
            Olá, {user?.username ? (user.username.split('@')[0].split('.')[0].charAt(0).toUpperCase() + user.username.split('@')[0].split('.')[0].slice(1)) : 'Gestor'}! 
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 400, maxWidth: 480, mt: 0.75 }}>
            Bem-vindo ao HCP — Hub de Controle da Produção. Como está a produtividade hoje?
          </Typography>
        </Box>

        {/* Círculos decorativos */}
        <Box sx={{ 
          position: 'absolute', right: -50, top: -50, width: 250, height: 250, 
          borderRadius: '50%', border: '40px solid rgba(255,255,255,0.05)', zIndex: 1 
        }} />
        <Box sx={{ 
          position: 'absolute', right: 50, bottom: -80, width: 180, height: 180, 
          borderRadius: '50%', border: '30px solid rgba(255,255,255,0.03)', zIndex: 1 
        }} />
      </Paper>

      {loadError && !stats.loading && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {failedModules.length > 0
            ? `Falha ao carregar: ${failedModules.join(', ')}. Os demais indicadores podem estar incompletos neste ambiente.`
            : 'Não foi possível carregar o dashboard. Use o botão atualizar para tentar novamente.'}
        </Alert>
      )}

      {/* Grid de Indicadores */}
      <Grid2 container spacing={3} sx={{ mb: 6 }}>
        <Grid2 size={{ xs: 12, sm: 6 }}>
          <IndicatorCard 
            title="Equipe HCP" 
            value={stats.collaborators} 
            icon={<MdPeople size={24} />} 
            color={theme.palette.hcpDashboard.team} 
            loading={stats.loading}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6 }}>
          <IndicatorCard 
            title="Operação" 
            value="—" 
            helperText="Indisponível até integração com a API"
            icon={<MdVerifiedUser size={24} />} 
            color={theme.palette.hcpDashboard.operation} 
          />
        </Grid2>
      </Grid2>

      <Typography variant="h6" fontWeight={800} sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <MdOutlineDashboard color={theme.palette.primary.main} /> Atalhos de Ação Rápida
      </Typography>

      <Grid2 container spacing={2}>
        <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
          <ShortcutCard 
            title="Gerenciar Prazos" 
            subtitle="Configurar entregas e fluxos" 
            icon={<MdLocalShipping size={24} />} 
            path="/hcp/configuracoes" 
            color={theme.palette.primary.main}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
          <ShortcutCard 
            title="Equipe de Produção" 
            subtitle="Controlar acessos e permissões" 
            icon={<MdPeople size={24} />} 
            path="/hcp/colaboradores" 
            color={theme.palette.hcpDashboard.team}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
          <ShortcutCard 
            title="Fluxo de Artes" 
            subtitle="Monitorar criações em andamento" 
            icon={<MdBrush size={24} />} 
            path="/hcp/arte-andamento" 
            color={theme.palette.hcpDashboard.artFlow}
            badge={stats.arteFinalCount}
            badgeColor={theme.palette.hcpDashboard.artFlow}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
          <ShortcutCard 
            title="Montagem de Cartelas" 
            subtitle="Otimização de tiragens" 
            icon={<MdLabel size={24} />} 
            path="/hcp/montagem-cartelas" 
            color={theme.palette.hcpDashboard.sheetAssembly}
            badge={stats.montagemCount}
            badgeColor={theme.palette.hcpDashboard.sheetAssembly}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
          <ShortcutCard 
            title="Fluxo de Produção" 
            subtitle="Gestão física de itens" 
            icon={<MdOutlinePrecisionManufacturing size={24} />} 
            path="/hcp/producao" 
            color={theme.palette.hcpDashboard.production}
            badge="Em Breve"
            badgeColor={theme.palette.hcpDashboard.badgeSoon}
            disabled
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
          <ShortcutCard 
            title="Logística HCP" 
            subtitle="Expedição e triagem" 
            icon={<MdOutlineDashboard size={24} />} 
            path="/hcp/logistica" 
            color={theme.palette.hcpDashboard.logistics}
            badge="Em Breve"
            badgeColor={theme.palette.hcpDashboard.badgeSoon}
            disabled
          />
        </Grid2>
        <Can permissions={['feature:hcp-gestao-fluxo:manage']}>
          <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
            <ShortcutCard 
              title="Gestão de Fluxo" 
              subtitle="Kanban dos fluxos HCP" 
              icon={<MdOutlineAccountTree size={24} />} 
              path="/hcp/gestao-fluxo" 
              color={theme.palette.hcpDashboard.flowManagement}
            />
          </Grid2>
        </Can>
      </Grid2>

    </Box>
  )
}

export default HCPDashboard
